<template>
  <div class="eudr-map-wrap" @mouseleave="onPolygonHoverEnd">
    <div :id="mapId" data-testid="eudr-map" :style="{ width: '100%', height }"></div>
  </div>
</template>

<script>
import * as atlas from 'azure-maps-control'
import {
  azureMapsApiKey,
  createPolygonLayersForDataSource,
  PolygonStyle,
  zoomToFeature,
  mapResizeTimeout,
  defaultCoordinates,
  disposeMap
} from '@/utils/map/MapHelpers'
import { uniqueDialogId } from '@/utils/componentHelpers'
import { featureDisplayName, isSyntheticParcelId, tractTypeLabel } from '@/utils/eudr/EudrFeatureName.js'
import { pointInGeometry } from '@/utils/eudr/EudrGeometry.js'

// Styling reads conflictActive (refreshStackBadges), not the static flag, so exclusion clears amber live.
const EXCLUDED_EXPR = ['boolean', ['get', 'excluded'], false]
const CONFLICT_EXPR = ['boolean', ['get', 'conflictActive'], false]
const POLYGON_FILTER = ['any', ['==', ['geometry-type'], 'Polygon'], ['==', ['geometry-type'], 'MultiPolygon']]
const POINT_FILTER = ['==', ['geometry-type'], 'Point']

export default {
  name: 'EudrMap',

  props: {
    // A GeoJSON FeatureCollection of Polygon/MultiPolygon features (all tracts in the package).
    featureCollection: {
      type: Object,
      required: true
    },
    // CSS height of the map canvas. Defaults to the full-dialog height; the upload preview passes a compact value.
    height: {
      type: String,
      default: '70vh'
    },
    // Validation mode: hover cards, feature-click events, and conflict/excluded styling.
    interactive: {
      type: Boolean,
      default: false
    },
    // Opens zoomed to this feature instead of the whole collection, restoring place after a remount.
    focusFeatureId: {
      type: [String, Number],
      required: false,
      default: null
    }
  },

  data: () => ({
    mapId: uniqueDialogId('eudr-map'),
    map: null,
    popup: null,
    source: null,
    stackSource: null,
    hoverLabelSource: null,
    hoveredFeature: null,
    pointDrag: null,
    // The click that follows a completed drag would toggle the point's exclusion.
    suppressNextClick: false
  }),

  watch: {
    '$vuetify.breakpoint.width' () { this.resizeMap() },
    '$vuetify.breakpoint.height' () { this.resizeMap() }
  },

  mounted () {
    this.$nextTick(() => this.initMap())
  },

  beforeDestroy () {
    document.removeEventListener('mousemove', this.onDocumentMouseMove)
    if (this.popup) {
      this.popup.close()
      this.popup = null
    }
    if (this.map) {
      disposeMap(this.map)
      this.map = null
    }
  },

  methods: {
    initMap () {
      this.map = new atlas.Map(this.mapId, {
        center: [defaultCoordinates.longitude, defaultCoordinates.latitude],
        zoom: 12,
        view: 'Auto',
        style: 'satellite_road_labels',
        authOptions: {
          authType: 'subscriptionKey',
          subscriptionKey: azureMapsApiKey()
        }
      })
      this.map.events.add('ready', this.onMapReady)
    },

    onMapReady () {
      if (!this.map) return
      this.source = new atlas.source.DataSource()
      this.map.sources.add(this.source)
      this.source.add(this.featureCollection)
      // Fill + outline layers, inserted under the basemap 'labels' so road labels stay on top.
      const layers = this.interactive ? this.createValidationLayers() : createPolygonLayersForDataSource(this.source, PolygonStyle.Cut)
      this.map.layers.add(layers, 'labels')
      const [fillLayer] = layers
      // Point-mode tracts (bare lat/long from a CSV) render as flags above the basemap labels.
      const pointLayer = this.createPointLayer()
      this.map.layers.add(pointLayer)

      this.popup = new atlas.Popup({ closeButton: false, showPointer: true, pixelOffset: [0, -8] })
      if (this.interactive) {
        this.map.events.add('mousemove', fillLayer, this.onPolygonHover)
        this.map.events.add('mouseleave', fillLayer, this.onPolygonHoverEnd)
        this.map.events.add('mousemove', pointLayer, this.onPolygonHover)
        // A point's geometry contains no cursor position, so its card closes on layer mouseleave instead.
        this.map.events.add('mouseleave', pointLayer, this.onPolygonHoverEnd)
        this.map.events.add('click', pointLayer, this.onInteractivePolygonClick)
        this.map.events.add('dblclick', pointLayer, this.onInteractivePolygonDblClick)
        // Symbol layers aren't draggable, so a wrong point (road, water, parking lot) is moved by hand here.
        this.map.events.add('mousedown', pointLayer, this.onPointMouseDown)
        // Map mouse events die once the cursor slides onto the HTML hover card; only document-level fires.
        document.addEventListener('mousemove', this.onDocumentMouseMove)
        // Panning/zooming moves the shapes out from under a stationary cursor; drop the card too.
        this.map.events.add('move', this.onPolygonHoverEnd)
        this.map.events.add('click', fillLayer, this.onInteractivePolygonClick)
        this.map.events.add('dblclick', fillLayer, this.onInteractivePolygonDblClick)
        this.initStackBadges()
      } else {
        // A bare-map click clears the popup; the fill-layer click (added after) re-opens it for the clicked tract.
        this.map.events.add('click', this.clearPopup)
        this.map.events.add('click', fillLayer, this.onPolygonClick)
        this.map.events.add('mousemove', fillLayer, this.setPointerCursor)
        this.map.events.add('mouseleave', fillLayer, this.resetCursor)
        this.map.events.add('click', pointLayer, this.onPolygonClick)
        this.map.events.add('mousemove', pointLayer, this.setPointerCursor)
        this.map.events.add('mouseleave', pointLayer, this.resetCursor)
        this.initPointHoverLabels(pointLayer)
      }

      // maxZoom keeps a single small tract from slamming to full zoom.
      if (this.featureCollection?.features?.length) {
        const focused = this.focusFeatureId != null ? this.findShape(this.focusFeatureId) : null
        if (focused) {
          const feature = focused.toJson()
          zoomToFeature(feature, this.map, { type: 'jump', maxZoom: this.zoomCapFor(feature) })
        } else {
          zoomToFeature(this.featureCollection, this.map, { type: 'jump', maxZoom: 16 })
        }
      }
      this.resizeMap()
    },

    createValidationLayers () {
      const fillLayer = new atlas.layer.PolygonLayer(this.source, null, {
        fillColor: ['case', EXCLUDED_EXPR, '#9E9E9E', CONFLICT_EXPR, '#FFC107', '#1E90FF'],
        fillOpacity: ['case', EXCLUDED_EXPR, 0.08, CONFLICT_EXPR, 0.35, 0.25],
        filter: POLYGON_FILTER
      })
      const lineLayer = new atlas.layer.LineLayer(this.source, null, {
        strokeColor: ['case', EXCLUDED_EXPR, '#BDBDBD', CONFLICT_EXPR, '#FFC107', '#FFFFFF'],
        strokeWidth: 2,
        strokeOpacity: ['case', EXCLUDED_EXPR, 0.5, 1],
        filter: POLYGON_FILTER
      })
      return [fillLayer, lineLayer]
    },

    // Names on hover only: always-on labels are noise once a composite runs to hundreds of points.
    createPointLayer () {
      const interactiveImage = ['case', EXCLUDED_EXPR, 'marker-black', CONFLICT_EXPR, 'marker-yellow', 'marker-blue']
      const opacity = this.interactive ? ['case', EXCLUDED_EXPR, 0.45, 1] : 1
      return new atlas.layer.SymbolLayer(this.source, null, {
        iconOptions: {
          image: this.interactive ? interactiveImage : 'marker-blue',
          anchor: 'bottom',
          allowOverlap: true,
          ignorePlacement: true,
          opacity
        },
        filter: POINT_FILTER
      })
    },

    // A private one-feature source: map-side property changes can leak into the persisted validated file.
    initPointHoverLabels (pointLayer) {
      this.hoverLabelSource = new atlas.source.DataSource()
      this.map.sources.add(this.hoverLabelSource)
      this.map.layers.add(new atlas.layer.SymbolLayer(this.hoverLabelSource, null, {
        iconOptions: { image: 'none' },
        textOptions: {
          textField: ['get', 'label'],
          color: '#FFFFFF',
          haloColor: '#000000',
          haloWidth: 1.5,
          size: 12,
          anchor: 'top',
          offset: [0, 0.4],
          allowOverlap: true,
          ignorePlacement: true
        }
      }))
      this.map.events.add('mousemove', pointLayer, this.onPointLabelHover)
      this.map.events.add('mouseleave', pointLayer, this.clearPointLabel)
    },

    onPointLabelHover (e) {
      const feature = this.shapeFeature(e)
      if (feature?.geometry?.type !== 'Point' || !this.hoverLabelSource) return
      const props = feature.properties ?? {}
      // Name on the first line, originating file on the second (external features only).
      const label = [props.productionPlace ?? '', props.origin !== 'internal' ? props.sourceFileName : null]
        .filter(Boolean).join('\n')
      this.hoverLabelSource.setShapes([{
        type: 'Feature',
        geometry: feature.geometry,
        properties: { label }
      }])
    },

    clearPointLabel () {
      if (this.hoverLabelSource) this.hoverLabelSource.setShapes([])
    },

    findShape (featureId) {
      return this.source?.getShapes().find(s => s.getProperties()?.featureId === featureId) ?? null
    },

    // Restyle one shape after the dialog toggles it; expressions pick the change up immediately.
    setFeatureExcluded (featureId, excluded) {
      const shape = this.findShape(featureId)
      if (shape) shape.setProperties({ ...shape.getProperties(), excluded })
      this.refreshStackBadges()
    },

    // A "×N" badge over each contested parcel, so reviewers can see how many shapes are stacked there.
    initStackBadges () {
      this.stackSource = new atlas.source.DataSource()
      this.map.sources.add(this.stackSource)
      this.map.layers.add(new atlas.layer.SymbolLayer(this.stackSource, null, {
        iconOptions: { image: 'none' },
        textOptions: {
          textField: ['get', 'stackLabel'],
          color: '#FFFFFF',
          haloColor: '#000000',
          haloWidth: 1.5,
          size: 16,
          allowOverlap: true,
          ignorePlacement: true
        },
        filter: ['==', ['geometry-type'], 'Point']
      }))
      this.refreshStackBadges()
    },

    // Recounted per toggle, so resolving a stack to one included shape drops both badge and amber styling.
    refreshStackBadges () {
      if (!this.interactive || !this.stackSource || !this.source) return
      const conflicted = []
      const stacks = new Map()
      this.source.getShapes().forEach(shape => {
        const props = shape.getProperties() ?? {}
        if (props.conflict !== true) return
        conflicted.push(shape)
        if (props.excluded === true || !props.parcelId) return
        if (!stacks.has(props.parcelId)) stacks.set(props.parcelId, [])
        stacks.get(props.parcelId).push(shape)
      })

      const activeParcels = new Set()
      const badges = []
      stacks.forEach((shapes, parcelId) => {
        if (shapes.length < 2) return
        activeParcels.add(parcelId)
        const bbox = atlas.data.BoundingBox.fromData({ type: 'FeatureCollection', features: shapes.map(s => s.toJson()) })
        badges.push(new atlas.data.Feature(
          new atlas.data.Point(atlas.data.BoundingBox.getCenter(bbox)),
          { stackLabel: `×${shapes.length}` }
        ))
      })
      this.stackSource.setShapes(badges)

      conflicted.forEach(shape => {
        const props = shape.getProperties()
        const active = props.excluded !== true && activeParcels.has(props.parcelId)
        if (props.conflictActive !== active) shape.setProperties({ ...props, conflictActive: active })
      })
    },

    // A Point has no extent, so maxZoom IS its zoom level; a looser cap keeps surrounding ground in view.
    zoomCapFor (feature) {
      return feature?.geometry?.type === 'Point' ? 15 : 17
    },

    zoomToFeatureId (featureId) {
      const shape = this.findShape(featureId)
      if (!shape || !this.map) return
      const feature = shape.toJson()
      zoomToFeature(feature, this.map, { maxZoom: this.zoomCapFor(feature) })
    },

    // Takes raw GeoJSON, because detail-dialog rows can span several features and may lack featureIds.
    zoomToGeojson (featureOrCollection) {
      if (this.map) zoomToFeature(featureOrCollection, this.map, { maxZoom: 17 })
    },

    onInteractivePolygonClick (e) {
      if (this.suppressNextClick) {
        this.suppressNextClick = false
        return
      }
      const props = this.shapeProperties(e)
      if (props?.featureId != null) this.$emit('feature-click', props.featureId)
    },

    onPointMouseDown (e) {
      const shape = e?.shapes?.[0]
      if (!shape || !e.pixel || typeof shape.setCoordinates !== 'function') return
      const feature = this.shapeFeature(e)
      if (feature?.geometry?.type !== 'Point' || feature.properties?.featureId == null) return
      this.pointDrag = { shape, featureId: feature.properties.featureId, startPixel: e.pixel, position: null }
      this.map.setUserInteraction({ dragPanInteraction: false })
      this.map.events.add('mousemove', this.onPointDragMove)
      this.map.events.add('mouseup', this.onPointDragEnd)
      this.map.events.add('mouseout', this.onPointDragEnd)
    },

    // A few pixels of slack keeps a plain click (include/exclude) from nudging the point.
    onPointDragMove (e) {
      const drag = this.pointDrag
      if (!drag || !e?.position || !e.pixel) return
      if (!drag.position) {
        const [startX, startY] = drag.startPixel
        if (Math.abs(e.pixel[0] - startX) < 4 && Math.abs(e.pixel[1] - startY) < 4) return
        this.onPolygonHoverEnd()
      }
      drag.position = e.position
      drag.shape.setCoordinates(e.position)
    },

    onPointDragEnd () {
      const drag = this.pointDrag
      if (!drag) return
      this.pointDrag = null
      this.map.events.remove('mousemove', this.onPointDragMove)
      this.map.events.remove('mouseup', this.onPointDragEnd)
      this.map.events.remove('mouseout', this.onPointDragEnd)
      this.map.setUserInteraction({ dragPanInteraction: true })
      if (!drag.position) return
      this.suppressNextClick = true
      setTimeout(() => { this.suppressNextClick = false })
      // Panning across the antimeridian can hand back a longitude beyond ±180 — wrap it back into range.
      const lon = ((drag.position[0] + 540) % 360) - 180
      const lat = Math.min(Math.max(drag.position[1], -90), 90)
      this.$emit('point-moved', { featureId: drag.featureId, coordinates: [lon, lat] })
    },

    // Re-sync one shape from its working feature after the dialog has mutated it (e.g. a moved point).
    syncFeature (featureId, feature) {
      const shape = this.findShape(featureId)
      if (!shape || !feature) return
      shape.setCoordinates(feature.geometry.coordinates)
      shape.setProperties({ ...shape.getProperties(), ...feature.properties })
      this.refreshStackBadges()
    },

    // Double-click = "edit this shape". The two single-click toggles it fires first cancel each other out.
    onInteractivePolygonDblClick (e) {
      if (e && typeof e.preventDefault === 'function') e.preventDefault()
      const props = this.shapeProperties(e)
      if (props?.featureId != null) this.$emit('feature-edit', props.featureId)
    },

    onPolygonHover (e) {
      this.setPointerCursor()
      // The flag follows the cursor during a drag, so hover would keep re-opening the card under it.
      if (this.pointDrag?.position) return
      const feature = this.shapeFeature(e)
      if (!feature) return
      this.hoveredFeature = feature
      this.popup.setOptions({
        content: this.validationPopupContent(feature),
        position: e.position
      })
      this.popup.open(this.map)
    },

    onPolygonHoverEnd () {
      this.hoveredFeature = null
      this.resetCursor()
      this.clearPopup()
    },

    // Fires even with the hover card under the cursor; fail-closed, so any error dismisses rather than sticks.
    onDocumentMouseMove (event) {
      if (!this.map || !this.hoveredFeature) return
      // Point flags have no interior for the containment test; their card closes on layer mouseleave.
      if (this.hoveredFeature.geometry?.type === 'Point') return
      try {
        const rect = this.map.getCanvasContainer().getBoundingClientRect()
        const pixel = [event.clientX - rect.left, event.clientY - rect.top]
        if (pixel[0] < 0 || pixel[1] < 0 || pixel[0] > rect.width || pixel[1] > rect.height) {
          this.onPolygonHoverEnd()
          return
        }
        const [lon, lat] = this.map.pixelsToPositions([pixel])[0]
        if (!pointInGeometry(lon, lat, this.hoveredFeature.geometry)) this.onPolygonHoverEnd()
      } catch (e) {
        this.onPolygonHoverEnd()
      }
    },

    shapeProperties (e) {
      const shape = e?.shapes?.[0]
      if (!shape) return null
      return typeof shape.getProperties === 'function' ? shape.getProperties() : shape.properties
    },

    // Geometry is included because the display-name fallback chain needs a centroid.
    shapeFeature (e) {
      const shape = e?.shapes?.[0]
      if (!shape) return null
      if (typeof shape.toJson === 'function') return shape.toJson()
      return { type: 'Feature', properties: shape.properties, geometry: shape.geometry }
    },

    onPolygonClick (e) {
      const feature = this.shapeFeature(e)
      if (!feature) return
      this.popup.setOptions({
        content: this.popupContent(feature),
        position: e.position
      })
      this.popup.open(this.map)
    },

    // "Lat/Long: 35.380500, -80.022700" for Point features; null for anything else.
    pointLocationLine (feature) {
      if (feature.geometry?.type !== 'Point') return null
      const [lon, lat] = feature.geometry.coordinates ?? []
      if (!Number.isFinite(lon) || !Number.isFinite(lat)) return null
      return `${this.$t('eudrLatLong')}: ${lat.toFixed(6)}, ${lon.toFixed(6)}`
    },

    // Uploaded features only: internal tract geometry has no originating file worth naming.
    sourceFileLine (props) {
      if (props.origin === 'internal' || !props.sourceFileName) return null
      return `${this.$t('eudrSourceFile')}: ${this.escapeHtml(props.sourceFileName)}`
    },

    parcelLine (props) {
      const parcel = props.parcelId && !isSyntheticParcelId(props.parcelId)
        ? this.escapeHtml(props.parcelId)
        : this.$t('notAvailable')
      return `${this.$t('parcelNumber')}: ${parcel}`
    },

    // Mirrors InteractiveMap's tract popup (cream bold text on a dark translucent panel).
    popupContent (feature) {
      const props = feature.properties ?? {}
      const css = 'color: #F9F6DF; font-weight: bold; font-size: 20px; padding: 7px;'
      const locationLine = this.pointLocationLine(feature)
      const fileLine = this.sourceFileLine(props)
      const interiorHtml = `
        <p id="map-marker-popup-name">${this.escapeHtml(featureDisplayName(feature))}</p>
        <hr class="map-marker-popup-divider">
        <p class="map-marker-popup-line">${locationLine ?? this.parcelLine(props)}</p>
        ${fileLine ? `<p class="map-marker-popup-line">${fileLine}</p>` : ''}
        <div class="tract-popup-tail"></div>`
      return `<div style='${css}'>${interiorHtml}</div>`
    },

    // The validation hover card: full provenance so contested parcels can be told apart at a glance.
    validationPopupContent (feature) {
      const props = feature.properties ?? {}
      const css = 'color: #F9F6DF; font-weight: bold; font-size: 20px; padding: 7px;'
      const title = this.escapeHtml(featureDisplayName(feature))
      const lines = []
      const locationLine = this.pointLocationLine(feature)
      if (locationLine) lines.push(locationLine)
      else lines.push(this.parcelLine(props))
      // A daemon-merged duplicate point carries every contributing account in accountNames.
      const accountLabel = Array.isArray(props.accountNames) && props.accountNames.length > 1
        ? props.accountNames.join(', ')
        : props.accountName
      if (accountLabel) lines.push(`${this.$t('account')}: ${this.escapeHtml(accountLabel)}`)
      const fileLine = this.sourceFileLine(props)
      if (fileLine) lines.push(fileLine)
      lines.push(this.originLine(props))
      if (props.producerName) lines.push(`${this.$t('eudrProducerName')}: ${this.escapeHtml(props.producerName)}`)
      if (props.producerCountry) lines.push(`${this.$t('eudrProducerCountry')}: ${this.escapeHtml(props.producerCountry)}`)
      if (props.area != null) lines.push(`${this.$t('eudrAreaHa')}: ${this.escapeHtml(props.area)}`)
      if (props.harvestStart || props.harvestEnd) {
        lines.push(`${this.$t('eudrHarvestWindow')}: ${this.escapeHtml(props.harvestStart ?? '?')} – ${this.escapeHtml(props.harvestEnd ?? '?')}`)
      }
      const flags = []
      if (props.conflict) flags.push(this.$t('eudrConflictChip'))
      if (props.excluded) flags.push(this.$t('eudrExcludedChip'))
      const lineHtml = lines.map(l => `<p class="map-marker-popup-line">${l}</p>`).join('')
      const flagHtml = flags.length ? `<p class="map-marker-popup-line" style="color:#FFC107;">${flags.join(' · ')}</p>` : ''
      return `<div style='${css}'>
        <p id="map-marker-popup-name">${title}</p>
        <hr class="map-marker-popup-divider">
        ${lineHtml}${flagHtml}
        <div class="tract-popup-tail"></div>
      </div>`
    },

    originLine (props) {
      const origin = props.origin === 'internal' ? this.$t('eudrOriginInternal') : this.$t('eudrOriginExternal')
      const parts = [origin]
      const tractType = tractTypeLabel(props.tractType)
      if (tractType) parts.push(this.escapeHtml(tractType))
      if (props.uploadKind === 'csvParcel') parts.push(this.$t('eudrUploadKindCsvParcel'))
      if (props.uploadKind === 'csvPoint') parts.push(this.$t('eudrUploadKindCsvPoint'))
      if (props.uploadKind === 'csvPointBuffer') parts.push(this.$t('eudrUploadKindCsvPointBuffer'))
      if (props.uploadKind === 'csvPointDrawn') parts.push(this.$t('eudrUploadKindCsvPointDrawn'))
      return parts.join(' · ')
    },

    clearPopup () {
      if (this.popup) this.popup.close()
    },

    setPointerCursor () {
      if (this.map) this.map.getCanvasContainer().style.cursor = 'pointer'
    },

    resetCursor () {
      if (this.map) this.map.getCanvasContainer().style.cursor = 'grab'
    },

    escapeHtml (value) {
      return String(value).replace(/[&<>"']/g, c =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]))
    },

    // Azure Maps mis-measures its container when first painted inside a dialog; resize once it has laid out.
    resizeMap () {
      if (!this.map) return
      setTimeout(() => {
        if (this.map) this.map.map.resize()
      }, mapResizeTimeout)
    }
  }
}
</script>

<style scoped src="@/styles/map-scoped.css"/>
<style src="@/styles/map-global.css"/>
<style>
/* Scoped to the wrapper, not #interactive-map: these azure-maps classes render in the map container. */
.eudr-map-wrap .popup-collection-container .popup-container .popup-content-container {
  background: rgba(.1, .1, .1, 0.8) !important;
  animation: map-popup-fade-in 0.15s;
}

/* Click-through, or moving into the card freezes it there; !important because the library sets this inline. */
.eudr-map-wrap .popup-collection-container,
.eudr-map-wrap .popup-collection-container * {
  pointer-events: none !important;
}

/* The popup trails the cursor by a few pixels, so without this it captures the mouse and lingers. */
.eudr-map-wrap .popup-collection-container,
.eudr-map-wrap .popup-collection-container .popup-container {
  pointer-events: none;
}

.eudr-map-wrap #map-marker-popup-name {
  margin-bottom: 2px;
  font-size: 16px !important;
  font-family: 'Roboto', sans-serif;
}

.eudr-map-wrap hr.map-marker-popup-divider {
  border: #00000000;
  border-top: 1px solid rgba(255, 255, 255, 0.3);
}

.eudr-map-wrap .map-marker-popup-line {
  font-weight: normal;
  margin-top: 3px;
  margin-bottom: 3px;
  font-family: 'Roboto', sans-serif;
  font-size: 14px;
}

.eudr-map-wrap .tract-popup-tail {
  position: relative;
  bottom: -8px;
  display: block;
  margin: 0 auto;
  border-color: rgba(.1, .1, .1, 0.8) rgba(0, 0, 0, 0) rgba(0, 0, 0, 0) rgba(0, 0, 0, 0);
  border-style: solid;
  border-width: 12px;
  width: 0px;
  height: 0px;
}
</style>
